/**
 * ESS POS — Role-Based Application Shell & Navigation Layout
 *
 * Implements:
 * - Permission-aware workspace navigation (Cashier, Waiter, Stock Keeper, Manager, Owner, ESS Super Admin)
 * - Multi-role switcher for employees assigned more than one role
 * - Responsive Desktop Sidebar, Tablet Collapsible Rail, and Mobile Bottom Navigation
 * - Tenant context indicator, Subscription Lockout / Grace Period alerts, Notifications & Profile
 */

import { apiClient } from '../api/apiClient';
import { WorkspaceId } from '../types/contracts';
import { escapeHtml, openModal, showToast } from './ui';
import { openFastSwitchStaffModal, openLockStationModal } from '../workspaces/waiterWorkspace';

export interface NavItemConfig {
  id: string;
  label: string;
  badge?: string | number;
  iconSvg: string;
}

export const WORKSPACE_META: Record<
  WorkspaceId,
  {
    title: string;
    shortTitle: string;
    roleBadge: string;
    subtitle: string;
    navItems: NavItemConfig[];
  }
> = {
  CASHIER: {
    title: 'Cashier Workspace',
    shortTitle: 'Cashier POS',
    roleBadge: 'CASHIER',
    subtitle: 'High-speed POS billing, payment settlement & shift cash reconciliation',
    navItems: [
      {
        id: 'incoming-qr-orders',
        label: 'Incoming QR Orders',
        iconSvg: '<path d="M3 3h6v6H3zM15 3h6v6h-6zM3 15h6v6H3zM15 15h2v2h-2zM19 15h2v2h-2zM15 19h2v2h-2zM19 19h2v2h-2z"/>',
      },
      {
        id: 'pending-bills',
        label: 'Active Table Bills',
        iconSvg: '<circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 15"/>',
      },
      {
        id: 'pos-sale',
        label: 'New Sale (POS)',
        iconSvg: '<rect x="3" y="4" width="18" height="16" rx="2"/><line x1="7" y1="8" x2="17" y2="8"/><line x1="7" y1="12" x2="13" y2="12"/><line x1="7" y1="16" x2="11" y2="16"/>',
      },
      {
        id: 'dashboard',
        label: 'Cashier Dashboard',
        iconSvg: '<rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>',
      },
      {
        id: 'completed-sales',
        label: 'Completed Sales & Receipts',
        iconSvg: '<path d="M14 2H6a2 2 0 0 0-2 2v16l4-2 4 2 4-2 4 2V8z"/><line x1="8" y1="10" x2="16" y2="10"/><line x1="8" y1="14" x2="14" y2="14"/>',
      },
      {
        id: 'shift-drawer',
        label: 'Shift & Cash Drawer',
        iconSvg: '<rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/>',
      },
      {
        id: 'profile',
        label: 'My Cashier Profile',
        iconSvg: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
      },
    ],
  },
  WAITER: {
    title: 'Waiter Workspace',
    shortTitle: 'Waiter & Tables',
    roleBadge: 'WAITER',
    subtitle: 'Floor table management, dine-in ordering & real-time kitchen tracking',
    navItems: [
      {
        id: 'tables',
        label: 'Floor Table Layout',
        iconSvg: '<rect x="3" y="4" width="8" height="7" rx="1.5"/><rect x="13" y="4" width="8" height="7" rx="1.5"/><rect x="3" y="13" width="8" height="7" rx="1.5"/><rect x="13" y="13" width="8" height="7" rx="1.5"/>',
      },
      {
        id: 'order-create',
        label: 'Create Table Order',
        iconSvg: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
      },
      {
        id: 'kitchen-tracking',
        label: 'Kitchen Order Status',
        iconSvg: '<path d="M12 2a9 9 0 0 1 9 9H3a9 9 0 0 1 9-9z"/><path d="M2 15h20"/><path d="M6 19h12"/>',
      },
      {
        id: 'dashboard',
        label: 'Waiter Dashboard',
        iconSvg: '<path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3"/>',
      },
      {
        id: 'profile',
        label: 'My Waiter Profile',
        iconSvg: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
      },
    ],
  },
  STOCK_KEEPER: {
    title: 'Stock Keeper Workspace',
    shortTitle: 'Inventory & Stock',
    roleBadge: 'STOCK KEEPER',
    subtitle: 'Product inventory balances, goods receiving, stock movements & recipe deduction',
    navItems: [
      {
        id: 'dashboard',
        label: 'Stock Dashboard',
        iconSvg: '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>',
      },
      {
        id: 'inventory-table',
        label: 'Product Inventory',
        iconSvg: '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>',
      },
      {
        id: 'receive-stock',
        label: 'Receive Stock',
        iconSvg: '<path d="M12 3v12"/><polyline points="8 11 12 15 16 11"/><rect x="4" y="17" width="16" height="4" rx="1"/>',
      },
      {
        id: 'adjustments',
        label: 'Adjustments & Transfers',
        iconSvg: '<polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>',
      },
      {
        id: 'damaged-usage',
        label: 'Damaged & Kitchen Usage',
        iconSvg: '<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>',
      },
      {
        id: 'movements',
        label: 'Movement Ledger',
        iconSvg: '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>',
      },
      {
        id: 'suppliers-recipes',
        label: 'Suppliers & Recipes',
        iconSvg: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>',
      },
      {
        id: 'profile',
        label: 'My Stock Profile',
        iconSvg: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
      },
    ],
  },
  MANAGER: {
    title: 'Manager Workspace',
    shortTitle: 'Manager Control',
    roleBadge: 'MANAGER',
    subtitle: 'Operational supervision, sales analytics, staff performance & restricted approvals',
    navItems: [
      {
        id: 'overview',
        label: 'Business Overview',
        iconSvg: '<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>',
      },
      {
        id: 'approvals',
        label: 'Discounts & Approvals',
        iconSvg: '<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>',
      },
      {
        id: 'orders-sales',
        label: 'Orders & Sales Analytics',
        iconSvg: '<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>',
      },
      {
        id: 'staff-performance',
        label: 'Cashier & Waiter Perf.',
        iconSvg: '<path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><polyline points="17 11 19 13 23 9"/>',
      },
      {
        id: 'inventory-reports',
        label: 'Inventory & Products',
        iconSvg: '<rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>',
      },
      {
        id: 'staff-audit',
        label: 'Staff & Audit History',
        iconSvg: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
      },
      {
        id: 'profile',
        label: 'My Manager Profile',
        iconSvg: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
      },
    ],
  },
  OWNER: {
    title: 'Restaurant Owner Workspace',
    shortTitle: 'Restaurant Owner',
    roleBadge: 'RESTAURANT OWNER',
    subtitle: 'Executive financial performance, subscription billing, menu & multi-branch settings',
    navItems: [
      {
        id: 'executive',
        label: 'Executive Overview',
        iconSvg: '<polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>',
      },
      {
        id: 'subscription',
        label: 'Subscription & M-Pesa',
        iconSvg: '<rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>',
      },
      {
        id: 'menu-config',
        label: 'Menu & EasyMenu Sync',
        iconSvg: '<path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>',
      },
      {
        id: 'branches-staff',
        label: 'Branches, Roles & Staff',
        iconSvg: '<path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>',
      },
      {
        id: 'settings-audit',
        label: 'Payments & Audit Logs',
        iconSvg: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9"/>',
      },
    ],
  },
  SUPER_ADMIN: {
    title: 'ESS Super Admin Platform',
    shortTitle: 'ESS Platform Admin',
    roleBadge: 'ESS SUPER ADMIN',
    subtitle: 'Isolated multi-tenant SaaS administration, subscription governance & cluster health',
    navItems: [
      {
        id: 'tenants',
        label: 'Tenant Restaurants',
        iconSvg: '<rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>',
      },
      {
        id: 'add-restaurant',
        label: '+ Add New Restaurant',
        iconSvg: '<line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>',
      },
      {
        id: 'plans-billing',
        label: 'SaaS Plans & Webhooks',
        iconSvg: '<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>',
      },
      {
        id: 'platform-health',
        label: 'Infrastructure & Health',
        iconSvg: '<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>',
      },
      {
        id: 'support-audit',
        label: 'Tenant Support & Audit',
        iconSvg: '<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>',
      },
    ],
  },
};

export function renderAppShell(params: {
  workspace: WorkspaceId;
  activeTab: string;
  sidebarCollapsed: boolean;
  onSelectTab: (tabId: string) => void;
  onSwitchWorkspace: (ws: WorkspaceId) => void;
  onToggleSidebar: () => void;
  onLogout: () => void;
}): HTMLElement {
  const user = apiClient.getUser();
  const tenant = apiClient.getTenant();
  const allowedWorkspaces = apiClient.getAuthorizedWorkspaces(user);
  const meta = WORKSPACE_META[params.workspace];

  const shell = document.createElement('div');
  shell.className = `ess-app-shell ${params.sidebarCollapsed ? 'ess-sidebar-collapsed' : ''}`;

  const tenantStatus = tenant?.status || 'ACTIVE';
  const showSubscriptionBanner =
    !user?.isSuperAdmin && (tenantStatus === 'LOCKED_UNPAID' || tenantStatus === 'GRACE_PERIOD');

  shell.innerHTML = `
    <!-- Desktop & Tablet Sidebar Navigation -->
    <aside class="ess-sidebar" aria-label="Workspace Navigation">
      <div class="ess-sidebar-brand">
        <div class="ess-brand-mark">ESS</div>
        <div class="ess-brand-text">
          <div class="ess-brand-title">ESS POS</div>
          <div class="ess-brand-tagline">Work Smarter</div>
        </div>
        <button type="button" class="ess-sidebar-toggle" id="ess-sidebar-collapse-btn" title="Toggle Sidebar" aria-label="Toggle Sidebar">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="ess-sidebar-role-box">
        <div class="ess-role-pill">${escapeHtml(meta.roleBadge)}</div>
        <div class="ess-tenant-name" title="${escapeHtml(user?.isSuperAdmin ? 'ESS Global Platform' : tenant?.name || 'Restaurant Tenant')}">
          ${escapeHtml(user?.isSuperAdmin ? 'ESS Global Platform' : tenant?.name || 'Authorized Tenant')}
        </div>
      </div>

      <nav class="ess-sidebar-nav">
        ${meta.navItems
          .map(
            (item) => `
          <button
            type="button"
            class="ess-nav-item ${params.activeTab === item.id ? 'active' : ''}"
            data-nav-id="${escapeHtml(item.id)}"
          >
            <svg class="ess-nav-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              ${item.iconSvg}
            </svg>
            <span class="ess-nav-label">${escapeHtml(item.label)}</span>
            ${item.badge !== undefined ? `<span class="ess-nav-badge">${escapeHtml(item.badge)}</span>` : ''}
          </button>
        `
          )
          .join('')}
      </nav>

      <div class="ess-sidebar-footer">
        <div class="ess-user-mini">
          <div class="ess-user-avatar">${escapeHtml((user?.firstName?.[0] || 'U') + (user?.lastName?.[0] || ''))}</div>
          <div class="ess-user-meta">
            <div class="ess-user-name">${escapeHtml(`${user?.firstName || ''} ${user?.lastName || ''}`.trim())}</div>
            <div class="ess-user-email">${escapeHtml(user?.email || '')}</div>
          </div>
        </div>
        <div class="ess-sidebar-footer-actions">
          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="ess-profile-btn" style="flex:1;">Profile</button>
          <button type="button" class="ess-btn ess-btn-ghost ess-btn-sm" id="ess-logout-btn">Sign Out</button>
        </div>
      </div>
    </aside>

    <!-- Main Content Column -->
    <div class="ess-main-wrapper">
      <!-- Top Application Header -->
      <header class="ess-topbar">
        <div class="ess-topbar-left">
          <button type="button" class="ess-mobile-menu-btn" id="ess-mobile-sidebar-btn" aria-label="Toggle Menu">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <div>
            <div class="ess-workspace-heading">${escapeHtml(meta.title)}</div>
            <div class="ess-workspace-subheading">${escapeHtml(meta.subtitle)}</div>
          </div>
        </div>

        <div class="ess-topbar-right">
          ${
            user?.isSuperAdmin
              ? `
            <div class="ess-workspace-switcher" title="Switch platform operations workspace">
              <label for="ess-ws-select" class="ess-sr-only">Active Workspace</label>
              <select id="ess-ws-select" class="ess-select ess-select-sm">
                ${allowedWorkspaces
                  .map(
                    (ws) => `
                  <option value="${escapeHtml(ws)}" ${ws === params.workspace ? 'selected' : ''}>
                    ${escapeHtml(WORKSPACE_META[ws].shortTitle)}
                  </option>
                `
                  )
                  .join('')}
              </select>
            </div>
          `
              : `<span class="ess-role-pill" style="margin:0 4px;font-size:11px;">${escapeHtml(meta.roleBadge)}</span>`
          }

          <div class="ess-tenant-chip">
            <span class="ess-status-dot ${tenantStatus === 'ACTIVE' ? 'ess-dot-online' : 'ess-dot-warning'}"></span>
            <span>${escapeHtml(user?.isSuperAdmin ? 'Platform Cluster' : tenant?.slug || 'tenant')}</span>
          </div>

          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="ess-topbar-lock-btn" style="font-size:11px;padding:4px 8px;font-weight:700;" title="Lock POS Terminal Immediately with PIN">
            🔒 Lock Station
          </button>

          <button type="button" class="ess-btn ess-btn-secondary ess-btn-sm" id="ess-topbar-switch-staff" style="font-size:11px;padding:4px 8px;font-weight:700;" title="Fast Staff & Handover Switch">
            👤 Switch Staff
          </button>

          <button type="button" class="ess-icon-btn" id="ess-notifications-btn" title="Operational Notifications" aria-label="Operational Notifications">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            <span class="ess-notif-dot"></span>
          </button>
        </div>
      </header>

      ${
        showSubscriptionBanner
          ? `
        <div class="ess-subscription-banner ${tenantStatus === 'LOCKED_UNPAID' ? 'ess-banner-danger' : 'ess-banner-warning'}" role="alert">
          <div>
            <strong>${
              tenantStatus === 'LOCKED_UNPAID'
                ? 'Subscription Overdue — POS Mutations Locked (HTTP 402):'
                : 'Subscription Grace Period Active:'
            }</strong>
            <span>${
              tenantStatus === 'LOCKED_UNPAID'
                ? 'New sales and stock mutations are disabled until M-Pesa renewal is completed. Reports and billing remain accessible.'
                : 'Your billing cycle has ended. Please renew via M-Pesa within the 3-day grace window to avoid POS interruption.'
            }</span>
          </div>
          ${
            allowedWorkspaces.includes('OWNER')
              ? `<button type="button" class="ess-btn ess-btn-primary ess-btn-sm" id="ess-banner-renew-btn">Renew via M-Pesa</button>`
              : ''
          }
        </div>
      `
          : ''
      }

      <!-- Dynamic Workspace Viewport -->
      <main class="ess-workspace-content" id="ess-workspace-viewport"></main>

      <!-- Mobile Bottom Navigation Bar -->
      <nav class="ess-mobile-bottom-nav" aria-label="Mobile Workspace Navigation">
        ${meta.navItems
          .slice(0, 5)
          .map(
            (item) => `
          <button
            type="button"
            class="ess-mobile-nav-item ${params.activeTab === item.id ? 'active' : ''}"
            data-nav-id="${escapeHtml(item.id)}"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              ${item.iconSvg}
            </svg>
            <span>${escapeHtml(item.label.split(' ')[0])}</span>
          </button>
        `
          )
          .join('')}
      </nav>
    </div>
  `;

  // Bind navigation item clicks
  shell.querySelectorAll('[data-nav-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.getAttribute('data-nav-id');
      if (id) params.onSelectTab(id);
    });
  });

  // Bind workspace switcher for multi-role employees
  const wsSelect = shell.querySelector('#ess-ws-select') as HTMLSelectElement | null;
  wsSelect?.addEventListener('change', () => {
    params.onSwitchWorkspace(wsSelect.value as WorkspaceId);
  });

  // Bind sidebar collapse toggle
  shell.querySelector('#ess-sidebar-collapse-btn')?.addEventListener('click', params.onToggleSidebar);
  shell.querySelector('#ess-mobile-sidebar-btn')?.addEventListener('click', params.onToggleSidebar);

  // Bind subscription banner quick button
  shell.querySelector('#ess-banner-renew-btn')?.addEventListener('click', () => {
    params.onSwitchWorkspace('OWNER');
    params.onSelectTab('subscription');
  });

  // Bind Profile button in sidebar footer to open active workspace private profile tab
  shell.querySelector('#ess-profile-btn')?.addEventListener('click', () => {
    params.onSelectTab('profile');
  });

  // Bind Station Lock
  shell.querySelector('#ess-topbar-lock-btn')?.addEventListener('click', () => {
    openLockStationModal(user, apiClient.getEffectiveTenantId());
  });

  // Bind Notifications modal
  shell.querySelector('#ess-notifications-btn')?.addEventListener('click', () => {
    openModal({
      title: 'Operational Alerts & Notifications',
      subtitle: `Active workspace alerts for ${meta.title}`,
      width: 'md',
      bodyHtml: `
        <div class="ess-stack-sm">
          <div class="ess-alert-card ess-alert-orange">
            <strong>Kitchen Ready Alert</strong>
            <p>Order KOT-2041 (Table T-01) is marked READY for serving.</p>
          </div>
          <div class="ess-alert-card ess-alert-warning">
            <strong>Low Stock Threshold</strong>
            <p>Swahili Chicken Biryani (MNC-003) has 4 portions remaining (Reorder threshold: 5).</p>
          </div>
          <div class="ess-alert-card ess-alert-neutral">
            <strong>Outbox &amp; Gateway Sync</strong>
            <p>All POS receipts and M-Pesa webhook callbacks are synchronized.</p>
          </div>
        </div>
      `,
    });
  });

  // Bind Switch Staff
  shell.querySelector('#ess-topbar-switch-staff')?.addEventListener('click', () => {
    openFastSwitchStaffModal(apiClient.getEffectiveTenantId());
  });

  // Bind Logout
  shell.querySelector('#ess-logout-btn')?.addEventListener('click', () => {
    params.onLogout();
    showToast({
      title: 'Signed Out Safely',
      message: 'Session tokens and tenant cache cleared.',
      type: 'info',
    });
  });

  return shell;
}

